import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Modal,
  Stack,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { PreLoader } from "./subcomponents/Preloader";
import "../components/styles/popupStyle.css";
import "../components/styles/dashboard.css";
import {
  GolfPropertyEnum,
  pagesList,
  propertyInfromationRequiredFields,
  requiredFields,
  ReservePropertyEnum,
  RetailPropertyEnum,
  SpaPropertyEnum,
  SnCPropertyEnum,
  TooltipAdminNavLabel,
} from "../constants/NavbarList";
import { RoomPropertyEnum } from "../constants/NavbarList";
import TooltipAdminPage from "./pages/admin/TooltipAdminPage";
import { TooltipProvider } from "../tooltips/TooltipContext";
import { tooltipProductCodeForSession } from "../tooltips/tooltipCatalog";
import PropertyInformation from "./pages/common/PropertyInformation";
import RetailPropertyInformation from "./pages/Retail/PropertyInformation";
import RetailCategoryGroups from "./pages/Retail/RetailCategoryGroups";
import RetailRetailCategory from "./pages/Retail/RetailRetailCategory";
import RetailRetailSubCategory from "./pages/Retail/RetailRetailSubCategory";
import RetailCategoryLinking from "./pages/Retail/RetailCategoryLinking";
import RetailPaymentMethod from "./pages/Retail/RetailPaymentMethod";
import RetailTaxes from "./pages/Retail/RetailTaxes";
import RetailDiscountTypes from "./pages/Retail/RetailDiscountTypes";
import RetailDiscountReasons from "./pages/Retail/RetailDiscountReasons";
import RetailVendorTypes from "./pages/Retail/RetailVendorTypes";
import RetailVendorSetup from "./pages/Retail/RetailVendorSetup";
import RetailRetailItems from "./pages/Retail/RetailRetailItems";
import RetailQuickSaleCategory from "./pages/Retail/RetailQuickSaleCategory";
import RetailOutletSetup from "./pages/Retail/RetailOutletSetup";
import RetailRoleSetup from "./pages/Retail/RetailRoleSetup";
import RetailUserSetup from "./pages/Retail/RetailUserSetup";
import BuildingCodes from "./pages/Versa/BuildingCodes";
import RoomFeatures from "./pages/Versa/RoomFeatures";
import RoomLocation from "./pages/Versa/RoomLocation";
import HouseKeepingSections from "./pages/Versa/HouseKeepingSections";
import RoomtypeMaintainence from "./pages/Versa/RoomtypeMaintainence";
import RoomtypeDefinition from "./pages/Versa/RoomTypeDefinitions";
import Department from "./pages/Versa/Department";
import TaxPostTypes from "./pages/Versa/TaxPostType";
import Category from "./pages/Versa/Category";
import Taxes from "./pages/Versa/Taxes";
import CommonTaxes from "./pages/common/Taxes";
import TaxExemptCategory from "./pages/Versa/TaxExemptCategory";
import Origin from "./pages/common/Origin";
import PaymentInformation from "./pages/Versa/PaymentInformation";
import GuestType from "./pages/common/GuestType";
import Transportation from "./pages/Versa/Transportation";
import PostTypes from "./pages/common/PostTypes";
import Segment1 from "./pages/common/Segment1";
import Segment2 from "./pages/common/Segment2";
import { ErrorPage } from "./ErrorPage";
import AlertComponent from "./subcomponents/AlertComponent";
import {
  useNavigate,
  Navigate,
  useSearchParams,
  Outlet,
} from "react-router-dom";
import {
  checkAuthStatus,
  createClientInformation,
  deleteClientInformation,
  getBuildings,
  getClientInformation,
  getImportProgress,
  getUserEmail,
  parseOnboardingSettings,
  resetImportProgress,
  saveImportProgress,
  sendWorkflowSubmitEmail,
  signoutUser,
  updateClientInformation,
  updateUserToken,
} from "./services/requests";
import { ExcelImportProgress, exportToExcel } from "../excel";
import ExcelReview from "./pages/ExcelReview/ExcelReview";
import {
  versaSampleData,
  golfSampleData,
  retailSampleData,
  spaSampleData,
  sncSampleData,
  reserveSampleData,
} from "../constants/SampleData";
import { getProductType, ProductEnum, SearchParams } from "../constants/login";
import { usesExcelReview } from "../excel/review/mode";
import {
  clearSessionReadOnly,
  isSessionReadOnly,
  setSessionReadOnly,
} from "../constants/sessionReadOnly";
import {
  canEditTooltips,
  canRoleSubmit,
  getWorkflowStatusLabel,
  inferWorkflowReadOnly,
  isCustomerRole,
  isInternalWorkflowRole,
  legacyRoleToCode,
  normalizeRoleCode,
  WorkflowRoleCode,
  WorkflowStatus,
} from "../constants/workflow";
import { removeSampleTestObjects } from "./actions/cleanup";
import {
  getWorkflowEmailStageForRole,
  parseWorkflowStakeholderEmails,
  resolveWorkflowEmailRecipients,
} from "./email/workflowEmailRecipients";
import { getImageForProperty } from "../constants/propertyType";
import GolfAllocationBlock from "./pages/Golf/GolfAllocationBlock";
import GolfCourses from "./pages/Golf/GolfCourses";
import GolfPlayerTypes from "./pages/Golf/GolfPlayerTypes";
import GolfRateTypes from "./pages/Golf/GolfRateTypes";
import GolfRatePlan from "./pages/Golf/GolfRatePlan";
// import GolfTeeTimeIntervalSetup from "./pages/Golf/GolfTeeTimeIntervalSetup";
// import GolfRatesSetup from "./pages/Golf/GolfRatesSetup";
import GolfRainCheckSetup from "./pages/Golf/GolfRainCheckSetup";
import GolfPlayerAllocation from "./pages/Golf/GolfPlayerAllocationBlock";
// import GolfTeeTimeIntervalTemplate from "./pages/Golf/GolfTeeTimeIntervalTemplate";
import GolfLessonLocation from "./pages/Golf/GolfLessonLocation";
import GolfCancelReasons from "./pages/Golf/GolfCancelReasons";
import GolfPlayerRate from "./pages/Golf/GolfPlayerRateBlock";
import CategoryGroups from "./pages/common/CategoryGroups";
import RoleSetup from "./pages/common/RoleSetup";
import OutletSetup from "./pages/common/OutletSetup";
import RetailCategories from "./pages/common/RetailCategories";
import RetailSubCategories from "./pages/common/RetailSubCategories";
import RetailItemsSetup from "./pages/common/RetailItemsSetup";
import SpaRetailSubCategories from "./pages/Spa/SpaRetailSubCategories";
import DiscountTypes from "./pages/common/DiscountTypes";
import QuickSaleCategories from "./pages/common/QuickSaleCategories";
import VendorTypes from "./pages/common/VendorTypes";
import DiscountReasons from "./pages/common/DiscountReasons";
import VendorSetup from "./pages/common/VendorSetup";
import SpaServiceGroups from "./pages/Spa/SpaServiceGroups";
import SpaTherapistSetup from "./pages/Spa/SpaTherapistSetup";
import SpaServiceLocations from "./pages/Spa/SpaServiceLocations";
import SpaServiceEquipment from "./pages/Spa/SpaServiceEquipment";
import SpaCancelReasons from "./pages/Spa/SpaCancelReasons";
import SpaServiceMedicalConditions from "./pages/Spa/SpaServiceMedicalConditions";
// import SpaNoShowReasons from "./pages/Spa/SpaNoShowReasons";
import SpaBreakTypes from "./pages/Spa/SpaBreakTypes";
import SpaLostDeniedReasons from "./pages/Spa/SpaLostDeniedReasons";
import SpaLogTypes from "./pages/Spa/SpaLogTypes";
import SpaGuestTypes from "./pages/Spa/SpaGuestTypes";
import SpaPriceType from "./pages/Spa/SpaPriceType";
import SpaServices from "./pages/Spa/SpaServices";
import SpaAddOns from "./pages/Spa/SpaAddOns";
import SpaDayPackages from "./pages/Spa/SpaDayPackages";
import SpaCategoryLinking from "./pages/Spa/SpaCategoryLinking";
import SpaPaymentMethods from "./pages/Spa/SpaPaymentMethods";
import SpaItemNumbersForServices from "./pages/Spa/SpaItemNumbersForServices";
import SpaItemNumbersForAddOns from "./pages/Spa/SpaItemNumbersForAddOns";
import SpaItemNumbersForDayPackages from "./pages/Spa/SpaItemNumbersForDayPackages";
// import SpaRetailItems from "./pages/Spa/SpaRetailItems";
import { resolveRetailItems } from "../constants/DefaultRetailItems";
import SnCManagers from "./pages/SnC/SnCManagers";
import SnCSalesCallsCallTypes from "./pages/SnC/SnCSalesCallsCallTypes";
import SnCSalesCallsNextSteps from "./pages/SnC/SnCSalesCallsNextSteps";
import SnCCancelCodes from "./pages/SnC/SnCCancelCodes";
import SnCContactType from "./pages/SnC/SnCContactType";
import SnCArrivalTypes from "./pages/SnC/SnCArrivalTypes";
import SnCReservedByCodes from "./pages/SnC/SnCReservedByCodes";
import SnCCancellationPolicy from "./pages/SnC/SnCCancellationPolicy";
import SnCPotentialCodes from "./pages/SnC/SnCPotentialCodes";
import SnCDenialCodes from "./pages/SnC/SnCDenialCodes";
import SnCDistributionList from "./pages/SnC/SnCDistributionList";
import SnCHolidaysAndHotDates from "./pages/SnC/SnCHolidaysAndHotDates";
import SnCFunctionRoomSetup from "./pages/SnC/SnCFunctionRoomSetup";
import SnCFunctionRoomTypeFee from "./pages/SnC/SnCFunctionRoomTypeFee";
import SnCFunctionTypes from "./pages/SnC/SnCFunctonTypes";
import SnCFunctionRoomsFeatures from "./pages/SnC/SnCFunctionRoomsFeatures";
import SnCFunctionRoom from "./pages/SnC/SnCFunctionRoom";
import SnCFoodTypes from "./pages/SnC/SnCFoodTypes";
import SnCUnitsOfMeasure from "./pages/SnC/SnCUnitsOfMeasure";
import SnCMenuItems from "./pages/SnC/SnCMenuItems";
import SnCSetupItems from "./pages/SnC/SnCSetupItems";
import SnCBeverageItems from "./pages/SnC/SnCBeverageItems";
import SnCMiscellaneousItems from "./pages/SnC/SnCMiscellaneousItems";
import SnCAudioVisualItems from "./pages/SnC/SnCAudioVisualItems";
import SnCRoomAndTax from "./pages/SnC/SnCRoomAndTax";
import SnCIncidentals from "./pages/SnC/SnCIncidentals";
// import SnCCompetitorProfile from "./pages/SnC/SnCCompetitorProfile"; // hidden for now
import ReservePropertyInformation from "./pages/ReserveCommon/ReservePropertyInformation";
import ReserveMiscItems from "./pages/ReserveCommon/ReserveMiscItems";
import ReservePropertyQuestions from "./pages/ReserveCommon/ReservePropertyQuestions";
import ReserveUserDetails from "./pages/ReserveCommon/ReserveUserDetails";
import ReserveRoles from "./pages/ReserveCommon/ReserveRoles";
import ReserveUserPermissions from "./pages/ReserveActivities/ReserveUserPermissions";
import ReserveDiningUserPermissions from "./pages/ReserveDining/ReserveUserPermissions";
import ReserveRatePlan from "./pages/ReserveCommon/ReserveRatePlan";
import ReserveServiceCharge from "./pages/ReserveCommon/ReserveServiceCharge";
import ReserveTaxes from "./pages/ReserveCommon/ReserveTaxes";
import ReserveLocation from "./pages/ReserveActivities/ReserveLocation";
import ReserveTables from "./pages/ReserveDining/ReserveTables";
import ReserveOutletSetup from "./pages/ReserveCommon/ReserveOutletSetup";
import ReserveReservationGuestNotes from "./pages/ReserveCommon/ReserveReservationGuestNotes";
import ReserveOperationHours from "./pages/ReserveCommon/ReserveOperationHours";
import ReserveActivities from "./pages/ReserveActivities/ReserveActivities";
import ReserveEquipmentsAddOns from "./pages/ReserveActivities/ReserveEquipmentsAddOns";
import ReserveInstructors from "./pages/ReserveActivities/ReserveInstructors";
import Questionnaire from "./questionnaire/Questionnaire";
// import CookieExpirationMonitor from "./subcomponents/CookieExpirationMonitor";

/** Matches onboarding chrome from Figma Make reference (dark sidebar, emerald accent). */
const PAGE_SUBTITLES: Record<string, string> = {
  "Property Information": "Enter your property details to get started",
};

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [incompleteFields, setIncompleteFields] = useState<string[]>([]);
  const [checkFields, setCheckFields] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [navbarPages, setNavbarPages] = useState<string[]>([]);
  const [userRoleCode, setUserRoleCode] = useState<string>(
    WorkflowRoleCode.CUSTOMER,
  );
  const [userDisplayName, setUserDisplayName] = useState<string>("");
  const [userRoleName, setUserRoleName] = useState<string>("");
  const [workflowStatus, setWorkflowStatus] = useState<string>(
    WorkflowStatus.DRAFT,
  );
  const [isReadOnlySession, setIsReadOnlySession] = useState(() =>
    isSessionReadOnly(),
  );
  const canSubmitWorkflow = useMemo(
    () => canRoleSubmit(userRoleCode, workflowStatus) && !isReadOnlySession,
    [userRoleCode, workflowStatus, isReadOnlySession],
  );
  const isTpmRole = useMemo(
    () => normalizeRoleCode(userRoleCode) === WorkflowRoleCode.TPM,
    [userRoleCode],
  );
  const [isCookiePopupEnabled, setIsCookiePopupEnabled] =
    useState<boolean>(false);
  const [isDeletModalEnabled, setIsDeleteModalEnabled] =
    useState<boolean>(false);
  const [isSubmitPopupEnabled, setIsSubmitPopupEnabled] =
    useState<boolean>(false);
  const [importProgress, setImportProgress] =
    useState<ExcelImportProgress | null>(null);
  const [isDmRunning, setIsDmRunning] = useState<boolean>(false);
  const [showExcelReview, setShowExcelReview] = useState<boolean>(false);
  const productId = Number(
    searchParams.get(SearchParams.PRODUCT as SearchParams),
  );
  const currentComponent = searchParams.get("component");
  const [displayQuestionnaire, setDisplayQuestionnaire] =
    useState<boolean>(false);
  const [displayTooltipAdmin, setDisplayTooltipAdmin] =
    useState<boolean>(false);
  /** Sales & Catering building dropdown labels from GetBuildings */
  const [sncBuildingOptions, setSncBuildingOptions] = useState<string[]>([]);
  const tenantIdParam = Number(searchParams.get(SearchParams.TENANT)) || 0;
  const propertyIdParam = Number(searchParams.get(SearchParams.PROPERTY)) || 0;

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // const handleCookieExpiring = (cookieInfo: any) => {
  //   setIsCookiePopupEnabled(true);
  // };

  /**
   * Shared onboarding settings are keyed by (tenantId, propertyId, productId) only —
   * never by userId. Always load via GetSettings after login / token refresh.
   * ImportProgress is optional (may 404 when the endpoint does not exist) and must
   * not gate or block applying GetSettings.
   */
  const loadSharedSettings = async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading !== false;
    const tenantId = Number(searchParams.get("tenantId")) || 0;
    const propertyId = Number(searchParams.get("propertyId")) || 0;
    const sessionProductId = Number(searchParams.get("productId")) || productId;
    if (showLoading) setLoading(true);
    try {
      const response: unknown = await getClientInformation(
        tenantId,
        propertyId,
        sessionProductId,
      );
      if (!(response instanceof Response)) {
        throw response instanceof Error
          ? response
          : new Error("GetSettings request failed");
      }
      if (response.status === 401) {
        await signoutUser();
        navigate("/login", { replace: true });
        return;
      }
      if (response.status !== 200) {
        return;
      }
      const data = await response.json();
      const settings = parseOnboardingSettings(data);
      if (settings.configValue === null) {
        setInitialPayload();
      } else {
        setPayload(settings.configValue);
      }
      const roleCode =
        settings.userRole?.code ??
        legacyRoleToCode(data.role ?? settings.userRoleCode);
      let effectiveRoleCode = roleCode;
      setUserRoleCode(roleCode);
      try {
        const userInfo = await getUserEmail();
        const first = userInfo?.firstName ?? "";
        const last = userInfo?.lastName ?? "";
        setUserDisplayName([first, last].filter(Boolean).join(" "));
        // Prefer nested role code; never set raw numeric `role` (e.g. 1) — that
        // breaks canEditTooltips / role checks which expect "implementationTeam".
        const profileRoleCode =
          (typeof userInfo?.userRole?.code === "string" &&
          userInfo.userRole.code.trim()
            ? userInfo.userRole.code.trim()
            : null) ??
          (userInfo?.userRoleCode != null
            ? legacyRoleToCode(userInfo.userRoleCode)
            : null) ??
          (userInfo?.role != null ? legacyRoleToCode(userInfo.role) : null);
        if (profileRoleCode) {
          effectiveRoleCode = profileRoleCode;
          setUserRoleCode(profileRoleCode);
        }
        setUserRoleName(
          userInfo?.userRole?.name ?? profileRoleCode ?? roleCode,
        );
      } catch {
        /* non-critical */
      }
      setWorkflowStatus(settings.status);
      const readOnly = inferWorkflowReadOnly(
        effectiveRoleCode,
        settings.status,
      );
      setSessionReadOnly(readOnly);
      setIsReadOnlySession(readOnly);
    } catch (err: unknown) {
      setAlertMessage(err instanceof Error ? err.toString() : String(err));
      setAlertType("error");
      setOpenAlert(true);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    const tenantId = Number(searchParams.get("tenantId")) || 0;
    const propertyId = Number(searchParams.get("propertyId")) || 0;
    const sessionProductId = Number(searchParams.get("productId")) || productId;

    void loadSharedSettings({ showLoading: true });

    // Optional excel-import resume state; 404 → null. Never blocks GetSettings.
    void getImportProgress(tenantId, propertyId, sessionProductId)
      .then((progress) => setImportProgress(progress))
      .catch(() => setImportProgress(null));

    setCurrentPage(
      currentComponent || currentComponent !== ""
        ? currentComponent
        : RoomPropertyEnum.propertyInformation,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load after login
  }, []);

  // SnC building dropdowns: cookie-auth GetBuildings when productId is 5.
  useEffect(() => {
    if (productId !== 5 || !tenantIdParam || !propertyIdParam) {
      setSncBuildingOptions([]);
      return;
    }
    let cancelled = false;
    void getBuildings(tenantIdParam, propertyIdParam, productId).then(
      (options) => {
        if (!cancelled) {
          setSncBuildingOptions(options);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [productId, tenantIdParam, propertyIdParam]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("component", currentPage ?? "");
      return next;
    });
  }, [currentPage]);

  const updateSettingsTemplate = async () => {
    if (isSessionReadOnly()) return;
    if (Object.keys(payload).length === 0) return;
    try {
      // const updatedPayload = removeSampleTestObjects(payload);
      const response: any = await updateClientInformation(
        payload,
        Number(searchParams.get("tenantId")) || 0,
        Number(searchParams.get("propertyId")) || 0,
        Number(searchParams.get("productId")) || 0,
      );
      if (response) {
        setAlertMessage("Draft saved Successfully");
        setAlertType("success");
        setOpenAlert(true);
      }
    } catch (err) {
      setAlertMessage(`error ${err}`);
      setAlertType("error");
      setOpenAlert(true);
    }
  };

  // This useEffect will auto-save the draft every 3 minutes while the component is mounted.
  // Auto-save now set to every 30 seconds for testing purposes
  useEffect(() => {
    let isUnmounted = false;
    let saving = false;
    const autoSave = async () => {
      if (isSessionReadOnly()) return;
      if (!isUnmounted && !saving && Object.keys(payload ?? {}).length > 0) {
        saving = true;
        try {
          await updateSettingsTemplate();
          setAlertMessage("Draft saved Successfully");
          setAlertType("success");
          setOpenAlert(true);
        } catch (err) {
          setAlertMessage(`error ${err}`);
          setAlertType("error");
          setOpenAlert(true);
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        } finally {
          saving = false;
        }
      }
    };

    const intervalId = setInterval(autoSave, 30 * 1000); // 30 seconds

    return () => {
      isUnmounted = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    const getNavbarPages = () => {
      const productType = getProductType(productId || 6);
      setNavbarPages(pagesList[productType as keyof typeof pagesList] ?? []);
    };
    getNavbarPages();
  }, []);

  const setInitialPayload = () => {
    // if (Object.keys(payload).length === 0) {
    //   const initialPayload: Record<string, any> = {};
    //   navbarPages.forEach((room) => {
    //     initialPayload[room] = null;
    //   });
    //   setPayload(initialPayload);
    // }
    const productType = getProductType(productId || 6);
    if (productType !== ProductEnum.TEST) {
      const pages = pagesList[productType as keyof typeof pagesList] ?? [];
      const mappedPayload: Record<string, any> = {};
      pages.forEach((page) => {
        if (productType === ProductEnum.VERSA) {
          mappedPayload[page] =
            versaSampleData[page as keyof typeof versaSampleData] || null;
        } else if (productType === ProductEnum.GOLF) {
          mappedPayload[page] =
            golfSampleData[page as keyof typeof golfSampleData] || null;
        } else if (productType === ProductEnum.RETAIL) {
          mappedPayload[page] =
            retailSampleData[page as keyof typeof retailSampleData] || null;
        } else if (productType === ProductEnum.SPA) {
          mappedPayload[page] =
            spaSampleData[page as keyof typeof spaSampleData] || null;
        } else if (productType === ProductEnum.SALES_AND_CATETING) {
          mappedPayload[page] =
            sncSampleData[page as keyof typeof sncSampleData] || null;
        } else if (
          productType === ProductEnum.RESERVE_ACTIVITIES ||
          productType === ProductEnum.RESERVE_DINING
        ) {
          mappedPayload[page] =
            reserveSampleData[page as keyof typeof reserveSampleData] || null;
        }
      });
      setPayload(mappedPayload);
    }
  };

  // if (typeof payload === "object" && Object.keys(payload).length === 0) {
  //   navbarPages.forEach((room) => {
  //     payload[room] = null;
  //   });
  // }

  const updatePayload = useCallback(
    async (
      key:
        | RoomPropertyEnum
        | GolfPropertyEnum
        | RetailPropertyEnum
        | SpaPropertyEnum
        | SnCPropertyEnum
        | ReservePropertyEnum,
      value: any,
    ) => {
      if (isSessionReadOnly()) return;
      if (await validateSignIn()) {
        setPayload((prevPayload) => {
          if (JSON.stringify(prevPayload[key]) === JSON.stringify(value)) {
            return prevPayload;
          }
          return {
            ...prevPayload,
            [key]: value,
          };
        });
      } else {
        setAlertMessage(
          "Your session is expired , please login again. Redirecting !!",
        );
        setAlertType("error");
        setOpenAlert(true);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    },
    [],
  );

  const handleDelete = async () => {
    if (isSessionReadOnly()) {
      setIsDeleteModalEnabled(false);
      return;
    }
    try {
      const response: any = await deleteClientInformation(
        Number(searchParams.get("tenantId")) || 0,
        Number(searchParams.get("propertyId")) || 0,
        Number(searchParams.get("productId")) || 0,
      );
      if (response.status === 200) {
        setAlertMessage("Data Deleted Successfully");
        setAlertType("success");
        setOpenAlert(true);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (err) {
      setAlertMessage(`Error , ${err}`);
      setAlertType("error");
      setOpenAlert(true);
    } finally {
      setOpenAlert(false);
    }
  };

  const handleSignout = async () => {
    try {
      clearSessionReadOnly();
      const response: any = await signoutUser();
      if (response.status === 200) {
        setAlertMessage("User signed out successfully");
        setAlertType("success");
        setOpenAlert(true);
        navigate("/login");
      }
    } catch (err) {
      setAlertMessage(`Error , ${err}`);
      setAlertType("error");
      setOpenAlert(true);
    } finally {
      setOpenAlert(false);
      window.location.reload();
    }
  };

  const getPage = () => {
    if (productId === 6) {
      switch (currentPage) {
        case RoomPropertyEnum.propertyInformation:
          return (
            <PropertyInformation
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[RoomPropertyEnum.propertyInformation]}
            />
          );
        case RoomPropertyEnum.buildingCodes:
          return (
            <BuildingCodes
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.buildingCodes, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.roomFeatures:
          return (
            <RoomFeatures
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.roomFeatures, value)
              }
              fieldValues={payload[RoomPropertyEnum.roomFeatures]}
            />
          );
        case RoomPropertyEnum.roomLocation:
          return (
            <RoomLocation
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.roomLocation, value)
              }
              fieldValues={payload[RoomPropertyEnum.roomLocation]}
            />
          );
        case RoomPropertyEnum.HousekeepingSections:
          return (
            <HouseKeepingSections
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.HousekeepingSections, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.roomTypeMaintainence:
          return (
            <RoomtypeMaintainence
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.roomTypeMaintainence, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.roomDefinitions:
          return (
            <RoomtypeDefinition
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.roomDefinitions, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.department:
          return (
            <Department
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.department, value)
              }
              fieldValues={payload[RoomPropertyEnum.department]}
            />
          );
        case RoomPropertyEnum.category:
          return (
            <Category
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.category, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.taxPostTypes:
          return (
            <TaxPostTypes
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.taxPostTypes, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.taxes:
          return (
            <Taxes
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.taxes, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.taxExemptCategory:
          return (
            <TaxExemptCategory
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.taxExemptCategory, value)
              }
              fieldValues={payload[RoomPropertyEnum.taxExemptCategory]}
            />
          );
        case RoomPropertyEnum.origin:
          return (
            <Origin
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.origin, value)
              }
              fieldValues={payload[RoomPropertyEnum.origin]}
            />
          );
        case RoomPropertyEnum.guestType:
          return (
            <GuestType
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.guestType, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.postTypes:
          return (
            <PostTypes
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.postTypes, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.Transportation:
          return (
            <Transportation
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.Transportation, value)
              }
              fieldValues={payload[RoomPropertyEnum.Transportation]}
            />
          );
        case RoomPropertyEnum.PaymentMethods:
          return (
            <PaymentInformation
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.PaymentMethods, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.Segment1:
          return (
            <Segment1
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.Segment1, value)
              }
              fieldValues={payload}
            />
          );
        case RoomPropertyEnum.Segment2:
          return (
            <Segment2
              data={(value: any) =>
                updatePayload(RoomPropertyEnum.Segment2, value)
              }
              fieldValues={payload}
            />
          );
        default:
          return <ErrorPage />;
      }
    } else if (productId === 2) {
      switch (currentPage) {
        case GolfPropertyEnum.propertyInformation:
          return (
            <PropertyInformation
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[GolfPropertyEnum.propertyInformation]}
            />
          );
        case GolfPropertyEnum.allocationBlocks:
          return (
            <GolfAllocationBlock
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.allocationBlocks, value)
              }
              fieldValues={payload[GolfPropertyEnum.allocationBlocks]}
            />
          );
        case GolfPropertyEnum.courses:
          return (
            <GolfCourses
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.courses, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.playerTypes:
          return (
            <GolfPlayerTypes
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.playerTypes, value)
              }
              fieldValues={payload[GolfPropertyEnum.playerTypes]}
            />
          );
        case GolfPropertyEnum.rateTypes:
          return (
            <GolfRateTypes
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.rateTypes, value)
              }
              fieldValues={payload[GolfPropertyEnum.rateTypes]}
            />
          );
        case GolfPropertyEnum.ratePlan:
          return (
            <GolfRatePlan
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.ratePlan, value)
              }
              fieldValues={payload}
              golfRetailItems={resolveRetailItems(
                payload[GolfPropertyEnum.retailItems],
              )}
            />
          );
        // commented out since we are not using these components now
        // case GolfPropertyEnum.teeTimeIntervalSetup:
        //   return (
        //     <GolfTeeTimeIntervalSetup
        //       data={(value: any) =>
        //         updatePayload(GolfPropertyEnum.teeTimeIntervalSetup, value)
        //       }
        //       fieldValues={payload}
        //     />
        //   );
        // case GolfPropertyEnum.rateSetup:
        //   return (
        //     <GolfRatesSetup
        //       data={(value: any) =>
        //         updatePayload(GolfPropertyEnum.rateSetup, value)
        //       }
        //       fieldValues={payload}
        //     />
        //   );
        // commented out since we are using these components now
        // case GolfPropertyEnum.tournamentFormat:
        //   return (
        //     <GolfTournamentFormat
        //       data={(value: any) =>
        //         updatePayload(GolfPropertyEnum.tournamentFormat, value)
        //       }
        //       fieldValues={payload[GolfPropertyEnum.tournamentFormat]}
        //     />
        //   );
        // case GolfPropertyEnum.rainCheckSetup:
        //   return (
        //     <GolfRainCheckSetup
        //       data={(value: any) =>
        //         updatePayload(GolfPropertyEnum.rainCheckSetup, value)
        //       }
        //       fieldValues={payload}
        //     />
        //   );
        case GolfPropertyEnum.roleSetup:
          return (
            <RoleSetup
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.roleSetup, value)
              }
              fieldValues={payload[GolfPropertyEnum.roleSetup]}
            />
          );
        case GolfPropertyEnum.outletSetup:
          return (
            <OutletSetup
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.outletSetup, value)
              }
              fieldValues={payload[GolfPropertyEnum.outletSetup]}
            />
          );
        case GolfPropertyEnum.categoryGroups:
          return (
            <CategoryGroups
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.categoryGroups, value)
              }
              fieldValues={payload[GolfPropertyEnum.categoryGroups]}
            />
          );
        case GolfPropertyEnum.retailCategories:
          return (
            <RetailCategories
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.retailCategories, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.retailSubCategories:
          return (
            <RetailSubCategories
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.retailSubCategories, value)
              }
              fieldValues={payload[GolfPropertyEnum.retailSubCategories]}
            />
          );
        case GolfPropertyEnum.retailItems:
          return (
            <RetailItemsSetup
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.retailItems, value)
              }
              fieldValues={payload}
              productType={ProductEnum.GOLF}
            />
          );
        case GolfPropertyEnum.taxes:
          return (
            <CommonTaxes
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.taxes, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.discountTypes:
          return (
            <DiscountTypes
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.discountTypes, value)
              }
              fieldValues={payload[GolfPropertyEnum.discountTypes]}
            />
          );
        case GolfPropertyEnum.quickSaleCategories:
          return (
            <QuickSaleCategories
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.quickSaleCategories, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.vendorTypes:
          return (
            <VendorTypes
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.vendorTypes, value)
              }
              fieldValues={payload[GolfPropertyEnum.vendorTypes]}
            />
          );
        case GolfPropertyEnum.discountReasons:
          return (
            <DiscountReasons
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.discountReasons, value)
              }
              fieldValues={payload[GolfPropertyEnum.discountReasons]}
            />
          );
        case GolfPropertyEnum.playerAllocationBlock:
          return (
            <GolfPlayerAllocation
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.playerAllocationBlock, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.vendorSetup:
          return (
            <VendorSetup
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.vendorSetup, value)
              }
              fieldValues={payload}
            />
          );
        // case GolfPropertyEnum.teeTimeIntervalTemplates:
        //   return (
        //     <GolfTeeTimeIntervalTemplate
        //       data={(value: any) =>
        //         updatePayload(GolfPropertyEnum.teeTimeIntervalTemplates, value)
        //       }
        //       fieldValues={payload}
        //     />
        //   );
        case GolfPropertyEnum.lessonLocation:
          return (
            <GolfLessonLocation
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.lessonLocation, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.cancelReasons:
          return (
            <GolfCancelReasons
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.cancelReasons, value)
              }
              fieldValues={payload}
            />
          );
        case GolfPropertyEnum.playerRateAllocation:
          return (
            <GolfPlayerRate
              data={(value: any) =>
                updatePayload(GolfPropertyEnum.playerRateAllocation, value)
              }
              fieldValues={payload}
            />
          );

        default:
          return <ErrorPage />;
      }
    } else if (productId === 3) {
      switch (currentPage) {
        case RetailPropertyEnum.propertyInformation:
          return (
            <RetailPropertyInformation
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[RetailPropertyEnum.propertyInformation]}
            />
          );
        case RetailPropertyEnum.outletSetup:
          return (
            <RetailOutletSetup
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.outletSetup, value)
              }
              fieldValues={payload[RetailPropertyEnum.outletSetup]}
            />
          );
        case RetailPropertyEnum.categoryGroups:
          return (
            <RetailCategoryGroups
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.categoryGroups, value)
              }
              fieldValues={payload[RetailPropertyEnum.categoryGroups]}
            />
          );
        case RetailPropertyEnum.retailCategories:
          return (
            <RetailRetailCategory
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.retailCategories, value)
              }
              fieldValues={payload[RetailPropertyEnum.retailCategories]}
            />
          );
        case RetailPropertyEnum.retailSubCategories:
          return (
            <RetailRetailSubCategory
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.retailSubCategories, value)
              }
              fieldValues={payload[RetailPropertyEnum.retailSubCategories]}
            />
          );
        case RetailPropertyEnum.categoryLinking:
          return (
            <RetailCategoryLinking
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.categoryLinking, value)
              }
              fieldValues={payload[RetailPropertyEnum.categoryLinking]}
            />
          );
        case RetailPropertyEnum.paymentMethods:
          return (
            <RetailPaymentMethod
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.paymentMethods, value)
              }
              fieldValues={payload[RetailPropertyEnum.paymentMethods]}
            />
          );
        case RetailPropertyEnum.taxes:
          return (
            <RetailTaxes
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.taxes, value)
              }
              fieldValues={payload[RetailPropertyEnum.taxes]}
            />
          );
        case RetailPropertyEnum.discountTypes:
          return (
            <RetailDiscountTypes
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.discountTypes, value)
              }
              fieldValues={payload[RetailPropertyEnum.discountTypes]}
            />
          );
        case RetailPropertyEnum.discountReasons:
          return (
            <RetailDiscountReasons
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.discountReasons, value)
              }
              fieldValues={payload[RetailPropertyEnum.discountReasons]}
            />
          );
        case RetailPropertyEnum.vendorTypes:
          return (
            <RetailVendorTypes
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.vendorTypes, value)
              }
              fieldValues={payload[RetailPropertyEnum.vendorTypes]}
            />
          );
        case RetailPropertyEnum.vendorSetup:
          return (
            <RetailVendorSetup
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.vendorSetup, value)
              }
              fieldValues={payload}
            />
          );
        case RetailPropertyEnum.retailItems:
          return (
            <RetailRetailItems
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.retailItems, value)
              }
              fieldValues={payload}
            />
          );
        case RetailPropertyEnum.quickSaleCategories:
          return (
            <RetailQuickSaleCategory
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.quickSaleCategories, value)
              }
              fieldValues={payload[RetailPropertyEnum.quickSaleCategories]}
            />
          );
        case RetailPropertyEnum.roleSetup:
          return (
            <RetailRoleSetup
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.roleSetup, value)
              }
              fieldValues={payload[RetailPropertyEnum.roleSetup]}
            />
          );
        case RetailPropertyEnum.userSetup:
          return (
            <RetailUserSetup
              data={(value: any) =>
                updatePayload(RetailPropertyEnum.userSetup, value)
              }
              fieldValues={payload[RetailPropertyEnum.userSetup]}
            />
          );
        default:
          return <ErrorPage />;
      }
    } else if (productId === 1) {
      switch (currentPage) {
        case SpaPropertyEnum.propertyInformation:
          return (
            <PropertyInformation
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[SpaPropertyEnum.propertyInformation]}
            />
          );
        case SpaPropertyEnum.categoryGroups:
          return (
            <CategoryGroups
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.categoryGroups, value)
              }
              fieldValues={payload[SpaPropertyEnum.categoryGroups]}
            />
          );
        case SpaPropertyEnum.outletSetup:
          return (
            <OutletSetup
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.outletSetup, value)
              }
              fieldValues={payload[SpaPropertyEnum.outletSetup]}
            />
          );
        case SpaPropertyEnum.retailCategories:
          return (
            <RetailCategories
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.retailCategories, value)
              }
              fieldValues={payload}
            />
          );
        case SpaPropertyEnum.retailSubCategories:
          return (
            <SpaRetailSubCategories
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.retailSubCategories, value)
              }
              fieldValues={payload[SpaPropertyEnum.retailSubCategories]}
            />
          );
        case SpaPropertyEnum.roleSetup:
          return (
            <RoleSetup
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.roleSetup, value)
              }
              fieldValues={payload[SpaPropertyEnum.roleSetup]}
            />
          );
        case SpaPropertyEnum.taxes:
          return (
            <CommonTaxes
              data={(value: any) => updatePayload(SpaPropertyEnum.taxes, value)}
              fieldValues={payload}
            />
          );
        case SpaPropertyEnum.discountTypes:
          return (
            <DiscountTypes
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.discountTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.discountTypes]}
            />
          );
        case SpaPropertyEnum.quickSaleCategories:
          return (
            <QuickSaleCategories
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.quickSaleCategories, value)
              }
              fieldValues={payload}
            />
          );
        case SpaPropertyEnum.vendorTypes:
          return (
            <VendorTypes
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.vendorTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.vendorTypes]}
            />
          );
        case SpaPropertyEnum.vendorSetup:
          return (
            <VendorSetup
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.vendorSetup, value)
              }
              fieldValues={payload}
            />
          );
        case SpaPropertyEnum.discountReasons:
          return (
            <DiscountReasons
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.discountReasons, value)
              }
              fieldValues={payload[SpaPropertyEnum.discountReasons]}
            />
          );
        case SpaPropertyEnum.serviceGroups:
          return (
            <SpaServiceGroups
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.serviceGroups, value)
              }
              fieldValues={payload[SpaPropertyEnum.serviceGroups]}
            />
          );
        case SpaPropertyEnum.therapistSetup:
          return (
            <SpaTherapistSetup
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.therapistSetup, value)
              }
              fieldValues={payload[SpaPropertyEnum.therapistSetup]}
            />
          );
        case SpaPropertyEnum.serviceLocations:
          return (
            <SpaServiceLocations
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.serviceLocations, value)
              }
              fieldValues={payload[SpaPropertyEnum.serviceLocations]}
            />
          );
        case SpaPropertyEnum.serviceEquipment:
          return (
            <SpaServiceEquipment
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.serviceEquipment, value)
              }
              fieldValues={payload[SpaPropertyEnum.serviceEquipment]}
            />
          );
        case SpaPropertyEnum.cancelReasons:
          return (
            <SpaCancelReasons
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.cancelReasons, value)
              }
              fieldValues={payload[SpaPropertyEnum.cancelReasons]}
            />
          );
        case SpaPropertyEnum.serviceMedicalConditions:
          return (
            <SpaServiceMedicalConditions
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.serviceMedicalConditions, value)
              }
              fieldValues={payload[SpaPropertyEnum.serviceMedicalConditions]}
            />
          );
        // case SpaPropertyEnum.noShowReasons:
        //   return (
        //     <SpaNoShowReasons
        //       data={(value: any) =>
        //         updatePayload(SpaPropertyEnum.noShowReasons, value)
        //       }
        //       fieldValues={payload[SpaPropertyEnum.noShowReasons]}
        //     />
        //   );
        case SpaPropertyEnum.breakTypes:
          return (
            <SpaBreakTypes
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.breakTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.breakTypes]}
            />
          );
        case SpaPropertyEnum.lostDeniedReasons:
          return (
            <SpaLostDeniedReasons
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.lostDeniedReasons, value)
              }
              fieldValues={payload[SpaPropertyEnum.lostDeniedReasons]}
            />
          );
        case SpaPropertyEnum.logTypes:
          return (
            <SpaLogTypes
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.logTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.logTypes]}
            />
          );
        case SpaPropertyEnum.guestTypes:
          return (
            <SpaGuestTypes
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.guestTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.guestTypes]}
            />
          );
        case SpaPropertyEnum.priceTypes:
          return (
            <SpaPriceType
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.priceTypes, value)
              }
              fieldValues={payload[SpaPropertyEnum.priceTypes]}
            />
          );
        case SpaPropertyEnum.services:
          return (
            <SpaServices
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.services, value)
              }
              fieldValues={payload}
              spaRetailItems={resolveRetailItems(
                payload[SpaPropertyEnum.retailItems],
              )}
            />
          );
        case SpaPropertyEnum.addOns:
          return (
            <SpaAddOns
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.addOns, value)
              }
              fieldValues={payload[SpaPropertyEnum.addOns]}
              spaRetailCategories={payload[SpaPropertyEnum.retailCategories]}
              spaRetailItems={resolveRetailItems(
                payload[SpaPropertyEnum.retailItems],
              )}
            />
          );
        case SpaPropertyEnum.dayPackages:
          return (
            <SpaDayPackages
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.dayPackages, value)
              }
              fieldValues={payload}
              spaRetailItems={resolveRetailItems(
                payload[SpaPropertyEnum.retailItems],
              )}
            />
          );
        case SpaPropertyEnum.categoryLinking:
          return (
            <SpaCategoryLinking
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.categoryLinking, value)
              }
              fieldValues={payload[SpaPropertyEnum.categoryLinking]}
              retailCategories={payload[SpaPropertyEnum.retailCategories]}
              retailSubCategories={payload[SpaPropertyEnum.retailSubCategories]}
            />
          );
        case SpaPropertyEnum.paymentMethods:
          return (
            <SpaPaymentMethods
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.paymentMethods, value)
              }
              fieldValues={payload[SpaPropertyEnum.paymentMethods]}
            />
          );
        case SpaPropertyEnum.itemNumbersForServices:
          return (
            <SpaItemNumbersForServices
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.itemNumbersForServices, value)
              }
              fieldValues={payload[SpaPropertyEnum.itemNumbersForServices]}
            />
          );
        case SpaPropertyEnum.itemNumbersForAddOns:
          return (
            <SpaItemNumbersForAddOns
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.itemNumbersForAddOns, value)
              }
              fieldValues={payload[SpaPropertyEnum.itemNumbersForAddOns]}
            />
          );
        case SpaPropertyEnum.itemNumbersForDayPackages:
          return (
            <SpaItemNumbersForDayPackages
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.itemNumbersForDayPackages, value)
              }
              fieldValues={payload[SpaPropertyEnum.itemNumbersForDayPackages]}
            />
          );
        case SpaPropertyEnum.retailItems:
          return (
            <RetailItemsSetup
              data={(value: any) =>
                updatePayload(SpaPropertyEnum.retailItems, value)
              }
              fieldValues={payload}
              productType={ProductEnum.SPA}
            />
          );
        default:
          return <ErrorPage />;
      }
    } else if (productId === 5) {
      switch (currentPage) {
        case SnCPropertyEnum.propertyInformation:
          return (
            <PropertyInformation
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[SnCPropertyEnum.propertyInformation]}
            />
          );
        case SnCPropertyEnum.outletSetup:
          return (
            <OutletSetup
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.outletSetup, value)
              }
              fieldValues={payload[SnCPropertyEnum.outletSetup]}
            />
          );
        case SnCPropertyEnum.categoryGroups:
          return (
            <CategoryGroups
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.categoryGroups, value)
              }
              fieldValues={payload[SnCPropertyEnum.categoryGroups]}
            />
          );
        case SnCPropertyEnum.retailCategories:
          return (
            <RetailCategories
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.retailCategories, value)
              }
              fieldValues={payload}
            />
          );
        case SnCPropertyEnum.retailItems:
          return (
            <RetailItemsSetup
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.retailItems, value)
              }
              fieldValues={payload}
              productType={ProductEnum.SALES_AND_CATETING}
            />
          );
        case SnCPropertyEnum.managers:
          return (
            <SnCManagers
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.managers, value)
              }
              fieldValues={payload[SnCPropertyEnum.managers]}
            />
          );
        case SnCPropertyEnum.salesCallsCallTypes:
          return (
            <SnCSalesCallsCallTypes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.salesCallsCallTypes, value)
              }
              fieldValues={payload[SnCPropertyEnum.salesCallsCallTypes]}
            />
          );
        case SnCPropertyEnum.salesCallsNextSteps:
          return (
            <SnCSalesCallsNextSteps
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.salesCallsNextSteps, value)
              }
              fieldValues={payload[SnCPropertyEnum.salesCallsNextSteps]}
            />
          );
        case SnCPropertyEnum.cancelCodes:
          return (
            <SnCCancelCodes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.cancelCodes, value)
              }
              fieldValues={payload[SnCPropertyEnum.cancelCodes]}
            />
          );
        case SnCPropertyEnum.contactType:
          return (
            <SnCContactType
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.contactType, value)
              }
              fieldValues={payload[SnCPropertyEnum.contactType]}
            />
          );
        case SnCPropertyEnum.arrivalTypes:
          return (
            <SnCArrivalTypes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.arrivalTypes, value)
              }
              fieldValues={payload[SnCPropertyEnum.arrivalTypes]}
            />
          );
        case SnCPropertyEnum.reservedByCodes:
          return (
            <SnCReservedByCodes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.reservedByCodes, value)
              }
              fieldValues={payload[SnCPropertyEnum.reservedByCodes]}
            />
          );
        case SnCPropertyEnum.cancellationPolicy:
          return (
            <SnCCancellationPolicy
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.cancellationPolicy, value)
              }
              fieldValues={payload[SnCPropertyEnum.cancellationPolicy]}
            />
          );
        case SnCPropertyEnum.potentialCodes:
          return (
            <SnCPotentialCodes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.potentialCodes, value)
              }
              fieldValues={payload[SnCPropertyEnum.potentialCodes]}
            />
          );
        case SnCPropertyEnum.denialCodes:
          return (
            <SnCDenialCodes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.denialCodes, value)
              }
              fieldValues={payload[SnCPropertyEnum.denialCodes]}
            />
          );
        case SnCPropertyEnum.distributionList:
          return (
            <SnCDistributionList
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.distributionList, value)
              }
              fieldValues={payload[SnCPropertyEnum.distributionList]}
            />
          );
        case SnCPropertyEnum.holidaysAndHotDates:
          return (
            <SnCHolidaysAndHotDates
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.holidaysAndHotDates, value)
              }
              fieldValues={payload[SnCPropertyEnum.holidaysAndHotDates]}
            />
          );
        case SnCPropertyEnum.functionRoomSetup:
          return (
            <SnCFunctionRoomSetup
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.functionRoomSetup, value)
              }
              fieldValues={payload[SnCPropertyEnum.functionRoomSetup]}
            />
          );
        case SnCPropertyEnum.functionRoomTypeFee:
          return (
            <SnCFunctionRoomTypeFee
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.functionRoomTypeFee, value)
              }
              fieldValues={payload[SnCPropertyEnum.functionRoomTypeFee]}
            />
          );
        case SnCPropertyEnum.functionTypes:
          return (
            <SnCFunctionTypes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.functionTypes, value)
              }
              fieldValues={payload[SnCPropertyEnum.functionTypes]}
            />
          );
        case SnCPropertyEnum.functionRoomsFeatures:
          return (
            <SnCFunctionRoomsFeatures
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.functionRoomsFeatures, value)
              }
              fieldValues={payload[SnCPropertyEnum.functionRoomsFeatures]}
            />
          );
        case SnCPropertyEnum.functionRoom:
          return (
            <SnCFunctionRoom
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.functionRoom, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.foodTypes:
          return (
            <SnCFoodTypes
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.foodTypes, value)
              }
              fieldValues={payload[SnCPropertyEnum.foodTypes]}
            />
          );
        case SnCPropertyEnum.unitsOfMeasure:
          return (
            <SnCUnitsOfMeasure
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.unitsOfMeasure, value)
              }
              fieldValues={payload[SnCPropertyEnum.unitsOfMeasure]}
            />
          );
        case SnCPropertyEnum.menuItems:
          return (
            <SnCMenuItems
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.menuItems, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.setupItems:
          return (
            <SnCSetupItems
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.setupItems, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.beverageItems:
          return (
            <SnCBeverageItems
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.beverageItems, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.miscellaneousItems:
          return (
            <SnCMiscellaneousItems
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.miscellaneousItems, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.audioVisualItems:
          return (
            <SnCAudioVisualItems
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.audioVisualItems, value)
              }
              fieldValues={payload}
              buildingOptions={sncBuildingOptions}
            />
          );
        case SnCPropertyEnum.roomAndTax:
          return (
            <SnCRoomAndTax
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.roomAndTax, value)
              }
              fieldValues={payload[SnCPropertyEnum.roomAndTax]}
            />
          );
        case SnCPropertyEnum.incidentals:
          return (
            <SnCIncidentals
              data={(value: any) =>
                updatePayload(SnCPropertyEnum.incidentals, value)
              }
              fieldValues={payload[SnCPropertyEnum.incidentals]}
            />
          );
        // case SnCPropertyEnum.competitorProfile: // hidden for now
        //   return (
        //     <SnCCompetitorProfile
        //       data={(value: any) =>
        //         updatePayload(SnCPropertyEnum.competitorProfile, value)
        //       }
        //       fieldValues={payload[SnCPropertyEnum.competitorProfile]}
        //     />
        //   );
        default:
          return <ErrorPage />;
      }
    } else if (productId === 12) {
      switch (currentPage) {
        case ReservePropertyEnum.propertyInformation:
          return (
            <ReservePropertyInformation
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[ReservePropertyEnum.propertyInformation]}
            />
          );
        case ReservePropertyEnum.outletSetup:
          return (
            <ReserveOutletSetup
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.outletSetup, value)
              }
              fieldValues={payload[ReservePropertyEnum.outletSetup]}
            />
          );
        case ReservePropertyEnum.miscItems:
          return (
            <ReserveMiscItems
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.miscItems, value)
              }
              fieldValues={payload[ReservePropertyEnum.miscItems]}
            />
          );
        case ReservePropertyEnum.propertyQuestions:
          return (
            <ReservePropertyQuestions
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.propertyQuestions, value)
              }
              fieldValues={payload[ReservePropertyEnum.propertyQuestions]}
            />
          );
        case ReservePropertyEnum.userDetails:
          return (
            <ReserveUserDetails
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.userDetails, value)
              }
              fieldValues={payload}
            />
          );
        case ReservePropertyEnum.roles:
          return (
            <ReserveRoles
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.roles, value)
              }
              fieldValues={payload[ReservePropertyEnum.roles]}
            />
          );
        case ReservePropertyEnum.userPermissions:
          return (
            <ReserveUserPermissions
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.userPermissions, value)
              }
              fieldValues={payload}
            />
          );
        case ReservePropertyEnum.ratePlan:
          return (
            <ReserveRatePlan
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.ratePlan, value)
              }
              fieldValues={payload[ReservePropertyEnum.ratePlan]}
            />
          );
        case ReservePropertyEnum.serviceCharge:
          return (
            <ReserveServiceCharge
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.serviceCharge, value)
              }
              fieldValues={payload[ReservePropertyEnum.serviceCharge]}
            />
          );
        case ReservePropertyEnum.taxes:
          return (
            <ReserveTaxes
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.taxes, value)
              }
              fieldValues={payload[ReservePropertyEnum.taxes]}
            />
          );
        case ReservePropertyEnum.location:
          return (
            <ReserveLocation
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.location, value)
              }
              fieldValues={payload[ReservePropertyEnum.location]}
            />
          );
        case ReservePropertyEnum.instructors:
          return (
            <ReserveInstructors
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.instructors, value)
              }
              fieldValues={payload[ReservePropertyEnum.instructors]}
            />
          );
        case ReservePropertyEnum.reservationGuestNotes:
          return (
            <ReserveReservationGuestNotes
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.reservationGuestNotes, value)
              }
              fieldValues={payload[ReservePropertyEnum.reservationGuestNotes]}
            />
          );
        case ReservePropertyEnum.operationHours:
          return (
            <ReserveOperationHours
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.operationHours, value)
              }
              fieldValues={payload[ReservePropertyEnum.operationHours]}
            />
          );
        case ReservePropertyEnum.equipmentsAddOns:
          return (
            <ReserveEquipmentsAddOns
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.equipmentsAddOns, value)
              }
              fieldValues={payload}
            />
          );
        case ReservePropertyEnum.activities:
          return (
            <ReserveActivities
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.activities, value)
              }
              fieldValues={payload}
            />
          );
        default:
          return <ErrorPage />;
      }
    } else if (productId === 13) {
      switch (currentPage) {
        case ReservePropertyEnum.propertyInformation:
          return (
            <ReservePropertyInformation
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.propertyInformation, value)
              }
              fieldValues={payload[ReservePropertyEnum.propertyInformation]}
            />
          );
        case ReservePropertyEnum.outletSetup:
          return (
            <ReserveOutletSetup
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.outletSetup, value)
              }
              fieldValues={payload[ReservePropertyEnum.outletSetup]}
            />
          );
        case ReservePropertyEnum.miscItems:
          return (
            <ReserveMiscItems
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.miscItems, value)
              }
              fieldValues={payload[ReservePropertyEnum.miscItems]}
            />
          );
        case ReservePropertyEnum.propertyQuestions:
          return (
            <ReservePropertyQuestions
              includeReservationSettings
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.propertyQuestions, value)
              }
              fieldValues={payload[ReservePropertyEnum.propertyQuestions]}
              reservationSettingsValues={
                payload[ReservePropertyEnum.reservationSettings]
              }
            />
          );
        case ReservePropertyEnum.userDetails:
          return (
            <ReserveUserDetails
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.userDetails, value)
              }
              fieldValues={payload}
            />
          );
        case ReservePropertyEnum.roles:
          return (
            <ReserveRoles
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.roles, value)
              }
              fieldValues={payload[ReservePropertyEnum.roles]}
            />
          );
        case ReservePropertyEnum.userPermissions:
          return (
            <ReserveDiningUserPermissions
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.userPermissions, value)
              }
              fieldValues={payload}
            />
          );
        case ReservePropertyEnum.ratePlan:
          return (
            <ReserveRatePlan
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.ratePlan, value)
              }
              fieldValues={payload[ReservePropertyEnum.ratePlan]}
            />
          );
        case ReservePropertyEnum.serviceCharge:
          return (
            <ReserveServiceCharge
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.serviceCharge, value)
              }
              fieldValues={payload[ReservePropertyEnum.serviceCharge]}
            />
          );
        case ReservePropertyEnum.taxes:
          return (
            <ReserveTaxes
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.taxes, value)
              }
              fieldValues={payload[ReservePropertyEnum.taxes]}
            />
          );
        case ReservePropertyEnum.tables:
          return (
            <ReserveTables
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.tables, value)
              }
              fieldValues={payload[ReservePropertyEnum.tables]}
            />
          );
        case ReservePropertyEnum.instructors:
          return (
            <ReserveInstructors
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.instructors, value)
              }
              fieldValues={payload[ReservePropertyEnum.instructors]}
            />
          );
        case ReservePropertyEnum.reservationGuestNotes:
          return (
            <ReserveReservationGuestNotes
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.reservationGuestNotes, value)
              }
              fieldValues={payload[ReservePropertyEnum.reservationGuestNotes]}
            />
          );
        case ReservePropertyEnum.operationHours:
          return (
            <ReserveOperationHours
              data={(value: any) =>
                updatePayload(ReservePropertyEnum.operationHours, value)
              }
              fieldValues={payload[ReservePropertyEnum.operationHours]}
            />
          );
        default:
          return <ErrorPage />;
      }
    }
    return <ErrorPage />;
  };

  const validateSections = () => {
    const missingFields = getIncompleteFields();
    if (missingFields.length > 0) {
      setAlertMessage(`Field ${missingFields.join(" , ")} is missing`);
      setOpenAlert(true);
      setAlertType("error");
      return true;
    }
    return false;
  };

  const getIncompleteFields = () => {
    var updatedPayload = removeSampleTestObjects(payload);
    const incompleteSections: string[] = [];
    requiredFields.map(
      (path) =>
        (updatedPayload[path] === null || updatedPayload[path]?.length === 0) &&
        incompleteSections.push(path),
    );
    validatePropertyInformation() &&
      incompleteSections.push(RoomPropertyEnum.propertyInformation);
    return incompleteSections;
  };

  const validatePropertyInformation = () => {
    const propertyInformation = payload[RoomPropertyEnum.propertyInformation];
    const missingFields =
      propertyInformation &&
      propertyInfromationRequiredFields?.filter(
        (field) => !propertyInformation[field],
      );
    if (missingFields?.length > 0) return true;
    return false;
  };

  const refreshSettingsAfterSubmit = async () => {
    await loadSharedSettings({ showLoading: false });
  };

  const SubmitPayload = async () => {
    if (isReadOnlySession || !canSubmitWorkflow) {
      setIsSubmitPopupEnabled(false);
      return;
    }
    const updatedPayload = removeSampleTestObjects(payload);
    const tenantId = Number(searchParams.get("tenantId")) || 0;
    const propertyId = Number(searchParams.get("propertyId")) || 0;
    const sessionProductId =
      Number(searchParams.get("productId")) || productId;
    try {
      setLoading(true);

      /*
       * CreateSettings advances the stage. The backend sets Status
       * (isFinalSubmit is hardcoded server-side). Do not send status.
       * Save Draft stays on UpdateSettings and does not advance.
       */
      const settingsResponse = await createClientInformation(
        updatedPayload,
        tenantId,
        propertyId,
        sessionProductId,
      );

      if (!(settingsResponse instanceof Response) || !settingsResponse.ok) {
        let detail = "CreateSettings request failed";
        if (settingsResponse instanceof Response) {
          const body = await settingsResponse.text().catch(() => "");
          detail =
            body.trim() ||
            `CreateSettings failed (${settingsResponse.status})`;
        } else if (settingsResponse instanceof Error) {
          detail = settingsResponse.message;
        } else if (settingsResponse != null) {
          detail = String(settingsResponse);
        }
        setAlertMessage(detail);
        setAlertType("error");
        setOpenAlert(true);
        return;
      }

      await refreshSettingsAfterSubmit();

      await exportToExcel({
        data: updatedPayload as Record<string, any[]>,
        filename: "Settings_Template.xlsx",
        productId,
      });
      const emailed = await sendWorkflowEmailForSubmittingRole();
      setAlertMessage(
        emailed
          ? `Submitted successfully. Excel downloaded and workflow notification emails were sent.`
          : `Submitted successfully. Excel downloaded. Workflow notification emails could not be sent.`,
      );
      setAlertType(emailed ? "success" : "error");
      setOpenAlert(true);
    } catch (err: any) {
      const message = err instanceof Error ? err.message : String(err);
      setAlertMessage(message);
      setAlertType("error");
      setOpenAlert(true);
    } finally {
      setIsSubmitPopupEnabled(false);
      setLoading(false);
    }
  };

  const sendWorkflowEmailForSubmittingRole = async (): Promise<boolean> => {
    const stage = getWorkflowEmailStageForRole(userRoleCode);
    if (!stage) return true;

    const profile = await getUserEmail();
    const stakeholders = parseWorkflowStakeholderEmails(profile);
    if (!stakeholders) {
      setAlertMessage(
        "Could not load recipient emails for workflow notification.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    const onboardingTeamEmail =
      process.env.REACT_APP_ONBOARDING_EMAIL ||
      "PMSOnboardingTeam@agilysys.com";
    const recipients = resolveWorkflowEmailRecipients(
      stage,
      stakeholders,
      onboardingTeamEmail,
    );
    if (recipients.length === 0) {
      setAlertMessage(
        "No valid recipient emails found for workflow notification.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    const profileRecord =
      profile && typeof profile === "object"
        ? (profile as Record<string, unknown>)
        : {};
    const customerName = String(profileRecord.customer ?? "Customer");

    const { response } = await sendWorkflowSubmitEmail({
      stage,
      toAddresses: recipients,
      productId,
      submitterFirstName: String(profileRecord.firstName ?? ""),
      submitterLastName: String(profileRecord.lastName ?? ""),
      customerName,
    });

    return Boolean(response?.ok);
  };

  const updateToken = async () => {
    try {
      const response: Response | Error = await updateUserToken(
        Number(searchParams.get("tenantId")) || 0,
        Number(searchParams.get("propertyId")) || 0,
        Number(searchParams.get("productId")) || 0,
      );
      if (!(response instanceof Response) || response.status !== 200) {
        const data =
          response instanceof Response
            ? await response.text().catch(() => "")
            : response.message;
        setAlertMessage(`Error : ${data}`);
        setAlertType("error");
        setOpenAlert(true);
        return;
      }
      // Reload shared settings after token refresh (independent of ImportProgress).
      await loadSharedSettings({ showLoading: true });
    } catch (err: unknown) {
      setAlertMessage(err instanceof Error ? err.message : String(err));
      setAlertType("error");
      setOpenAlert(true);
    }
  };

  const handleSubmit = async () => {
    if (isReadOnlySession) {
      setAlertMessage(
        "This session is read-only. You cannot submit or save changes.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!canSubmitWorkflow) {
      setAlertMessage(
        "Only the role assigned to the current workflow stage can submit.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateSections()) {
      setIsSubmitPopupEnabled(true);
    } else {
      setCheckFields(true);
    }
  };

  const handleSaveDraft = async () => {
    if (isSessionReadOnly()) {
      setAlertMessage(
        "This session is read-only. You cannot save drafts or submit changes.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    const authResponse: any = await checkAuthStatus();
    if (authResponse.isAuthenticated) {
      updateSettingsTemplate();
    } else {
      setIsCookiePopupEnabled(true);
    }
  };

  const validateSignIn = async () => {
    const authResponse: any = await checkAuthStatus();
    return authResponse.isAuthenticated;
  };

  const findNavbarIndex = () => {
    return navbarPages.findIndex((list) => currentPage === list);
  };

  useEffect(() => {
    const fields = getIncompleteFields();
    checkFields && setIncompleteFields(fields);
  }, [currentPage, checkFields, payload]);

  const pageSubtitle =
    (currentPage && PAGE_SUBTITLES[currentPage]) ??
    "Review and update the settings below to continue.";

  const getNavItemClassName = (list: string) => {
    const classes = ["dashboard-nav-item"];
    if (incompleteFields.includes(list))
      classes.push("dashboard-nav-item--missing");
    else if (
      currentPage === list &&
      !displayQuestionnaire &&
      !displayTooltipAdmin
    )
      classes.push("dashboard-nav-item--active");
    return classes.join(" ");
  };

  const canShowTooltipAdmin = canEditTooltips(userRoleCode);
  const sessionProductName =
    getProductType(productId || 6) ?? ProductEnum.VERSA;
  const tooltipProductCode = tooltipProductCodeForSession(sessionProductName);

  const sidebarContent = (
    <Box className="dashboard-sidebar">
      {isMobile && (
        <Box className="dashboard-sidebar-close-row">
          <IconButton
            onClick={handleDrawerToggle}
            className="dashboard-sidebar-close-icon"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <Stack className="dashboard-sidebar-logo">
        <img
          src={getImageForProperty(productId)}
          alt=""
          className="dashboard-sidebar-logo-img"
        />
      </Stack>
      <Stack className="dashboard-sidebar-nav-list">
        {navbarPages.map((list, index) => (
          <Typography
            id={
              incompleteFields.includes(list)
                ? `missing_field_${index}`
                : `navbarList${currentPage === list && !displayQuestionnaire && !displayTooltipAdmin ? "_active" : ""}_${index}`
            }
            variant="body2"
            key={index}
            className={getNavItemClassName(list)}
            ref={(el) => {
              if (
                currentPage === list &&
                !displayQuestionnaire &&
                !displayTooltipAdmin &&
                el
              ) {
                setTimeout(() => {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 0);
              }
            }}
            onClick={async () => {
              if (await validateSignIn()) {
                setCurrentPage(list);
                setDisplayQuestionnaire(false);
                setDisplayTooltipAdmin(false);
                if (isMobile) setMobileDrawerOpen(false);
              } else {
                window.location.reload();
              }
            }}
          >
            {list}
          </Typography>
        ))}
        {canShowTooltipAdmin && (
          <Typography
            variant="body2"
            className={`dashboard-nav-item-muted${
              displayTooltipAdmin ? " dashboard-nav-item--active" : ""
            }`}
            onClick={() => {
              setDisplayTooltipAdmin(true);
              setDisplayQuestionnaire(false);
              if (isMobile) setMobileDrawerOpen(false);
            }}
          >
            {TooltipAdminNavLabel}
          </Typography>
        )}
        {isInternalWorkflowRole(userRoleCode) && (
          <Typography
            variant="body2"
            className={`dashboard-nav-item-muted${
              displayQuestionnaire ? " dashboard-nav-item--active" : ""
            }`}
            onClick={() => {
              setDisplayQuestionnaire(true);
              setDisplayTooltipAdmin(false);
            }}
          >
            Questionnaire
          </Typography>
        )}
        <Typography
          variant="body2"
          className="dashboard-nav-item-muted dashboard-nav-item-logout"
          onClick={handleSignout}
        >
          Logout
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <TooltipProvider productName={String(sessionProductName)}>
    <>
      {loading ? (
        <PreLoader preloaderCheck={loading} />
      ) : (
        <Stack direction="row" className="dashboard-root">
          {/* Mobile Drawer */}
          <Drawer
            variant="temporary"
            open={mobileDrawerOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            className="dashboard-drawer"
          >
            {sidebarContent}
          </Drawer>

          {/* Desktop Sidebar */}
          <Stack direction="column" className="dashboard-sidebar-desktop">
            {sidebarContent}
          </Stack>

          <Box className="dashboard-main">
            {/* Top header (non-scrolling) */}
            <Box className="dashboard-header-area">
              <Box className="dashboard-mobile-header">
                <IconButton
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  className="dashboard-mobile-header-icon"
                >
                  <MenuIcon />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  noWrap
                  className="dashboard-mobile-header-title"
                >
                  {currentPage || "Dashboard"}
                </Typography>
                <Box className="dashboard-mobile-header-spacer" />
              </Box>
              {/* User info — always visible top right */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 10,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  }}
                >
                  {userDisplayName || "—"} &nbsp;|&nbsp;
                  {userRoleName || userRoleCode}
                </Typography>
              </Box>
              {!displayQuestionnaire && !displayTooltipAdmin && (
                <Box className="dashboard-page-title-desktop">
                  <Typography variant="h4" className="dashboard-page-title">
                    {currentPage || "Dashboard"}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="dashboard-page-subtitle"
                  >
                    {pageSubtitle}
                  </Typography>
                </Box>
              )}
              {displayTooltipAdmin && (
                <Box className="dashboard-page-title-desktop">
                  <Typography variant="h4" className="dashboard-page-title">
                    {TooltipAdminNavLabel} — {tooltipProductCode}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="dashboard-page-subtitle"
                  >
                    Edit {tooltipProductCode} instructional tooltip copy
                    (product-scoped, not per tenant).
                  </Typography>
                </Box>
              )}
              {!displayQuestionnaire && !displayTooltipAdmin && (
                <Typography
                  variant="body2"
                  className="dashboard-page-subtitle-mobile"
                >
                  {pageSubtitle}
                </Typography>
              )}
            </Box>

            {/* Scrollable content area */}
            <Box className="dashboard-content-area">
              {isReadOnlySession && (
                <Alert severity="warning" className="dashboard-readonly-alert">
                  Read-only mode: you can browse settings, but saving drafts,
                  submitting, and deleting are disabled.
                </Alert>
              )}
              {!isReadOnlySession && workflowStatus && (
                <Alert severity="info" className="dashboard-readonly-alert">
                  {getWorkflowStatusLabel(workflowStatus)}
                  {!canSubmitWorkflow &&
                    workflowStatus.toLowerCase() !== WorkflowStatus.SUBMITTED &&
                    " — your role cannot submit at this stage."}
                </Alert>
              )}
              {importProgress?.status === "failed" && (
                <Alert severity="warning" className="dashboard-readonly-alert">
                  Visual One import stopped at phase "
                  {importProgress.lastFailedPhase ?? "unknown"}"
                  {importProgress.lastError
                    ? `: ${importProgress.lastError}`
                    : ""}
                  . Settings are saved — submit again to resume import.
                </Alert>
              )}
              {displayTooltipAdmin ? (
                <TooltipAdminPage
                  key={tooltipProductCode}
                  productCode={tooltipProductCode}
                />
              ) : displayQuestionnaire ? (
                <Questionnaire productId={productId} />
              ) : (
                getPage()
              )}
            </Box>

            {/* Sticky footer */}
            {!displayQuestionnaire && !displayTooltipAdmin && (
            <Box className="dashboard-footer">
              <Stack className="dashboard-footer-row">
                <Button
                  id="previousButton"
                  variant="contained"
                  size="medium"
                  color="secondary"
                  disableElevation
                  disabled={findNavbarIndex() === 0}
                  onClick={async () => {
                    if (await validateSignIn()) {
                      setCurrentPage(navbarPages[findNavbarIndex() - 1]);
                    } else {
                      window.location.reload();
                    }
                  }}
                  className="dashboard-btn dashboard-btn-prev"
                >
                  Previous
                </Button>
                <Stack className="dashboard-footer-actions">
                  <Button
                    variant="contained"
                    size="medium"
                    color="info"
                    disabled={
                      isReadOnlySession || Object.keys(payload).length <= 1
                    }
                    onClick={handleSaveDraft}
                    className="dashboard-btn dashboard-btn-save"
                  >
                    Save Draft
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    color="error"
                    disabled={isReadOnlySession}
                    onClick={() => setIsDeleteModalEnabled(true)}
                    className="dashboard-btn dashboard-btn-delete"
                  >
                    Delete Information
                  </Button>
                  <Button
                    id="nextButton"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    disableElevation
                    disabled={findNavbarIndex() === navbarPages.length - 1}
                    onClick={async () => {
                      if (await validateSignIn()) {
                        setCurrentPage(navbarPages[findNavbarIndex() + 1]);
                      } else {
                        window.location.reload();
                      }
                    }}
                    className="dashboard-btn dashboard-btn-next"
                  >
                    Next
                  </Button>
                  {isTpmRole && usesExcelReview(productId) && (
                    <Button
                      id="excelReviewButton"
                      variant="contained"
                      size="medium"
                      color="success"
                      disableElevation
                      onClick={() => setShowExcelReview(true)}
                      disabled={
                        isReadOnlySession || Object.keys(payload).length <= 1
                      }
                      className="dashboard-btn dashboard-btn-submit"
                    >
                      Excel Review
                    </Button>
                  )}
                  <Button
                    id="submitButton"
                    variant="contained"
                    size="medium"
                    color="success"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={
                      isReadOnlySession ||
                      !canSubmitWorkflow ||
                      Object.keys(payload).length <= 1
                    }
                    className="dashboard-btn dashboard-btn-submit"
                  >
                    {isCustomerRole(userRoleCode)
                      ? "Final Review"
                      : "Final Submit"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
            )}
          </Box>
          <AlertComponent
            message={alertMessage}
            type={alertType}
            alertOpen={openAlert}
            closeAlert={() => setOpenAlert(false)}
          />
          {showExcelReview && (
            <Modal
              open={showExcelReview}
              onClose={() => setShowExcelReview(false)}
              aria-labelledby="excel-review-modal"
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90vw",
                  maxWidth: 1100,
                  maxHeight: "90vh",
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ pr: 1, pt: 0.5 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowExcelReview(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <ExcelReview
                  payload={payload as Record<string, unknown>}
                  tenantId={Number(searchParams.get("tenantId")) || 0}
                  propertyId={Number(searchParams.get("propertyId")) || 0}
                  productId={Number(searchParams.get("productId")) || 0}
                />
              </Box>
            </Modal>
          )}
          {/* <CookieExpirationMonitor
            onCookieExpiring={handleCookieExpiring}
            checkInterval={10000}
          /> */}
          {isCookiePopupEnabled && (
            <Modal
              open={isCookiePopupEnabled}
              onClose={() => setIsCookiePopupEnabled(false)}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box className="dashboard-modal-box">
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Logout due to inactivity
                </Typography>
                <Typography
                  id="modal-modal-description"
                  className="dashboard-modal-description"
                >
                  Your current session is going to be logged out due to
                  inactivity. To continue working click OK
                </Typography>
                <Box className="dashboard-modal-actions">
                  <Button variant="outlined" onClick={handleSignout}>
                    Sign out
                  </Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      await updateToken();
                      setIsCookiePopupEnabled(false);
                    }}
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
          {isDeletModalEnabled && (
            <Modal
              open={isDeletModalEnabled}
              onClose={() => setIsDeleteModalEnabled(false)}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box className="dashboard-modal-box">
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Delete all information
                </Typography>
                <Typography
                  id="modal-modal-description"
                  className="dashboard-modal-description"
                >
                  Are you sure to delete all the information ? This cannot be
                  undone !
                </Typography>
                <Box className="dashboard-modal-actions">
                  <Button variant="outlined" onClick={handleDelete}>
                    Yes
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setIsDeleteModalEnabled(false);
                    }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
          {isSubmitPopupEnabled && (
            <Modal
              open={isSubmitPopupEnabled}
              onClose={() => setIsSubmitPopupEnabled(false)}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box className="dashboard-modal-box">
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  {isTpmRole
                    ? "Confirm data migration complete"
                    : "Submit all information"}
                </Typography>
                <Typography
                  id="modal-modal-description"
                  className="dashboard-modal-description"
                >
                  {isTpmRole
                    ? "This will email the accounting team confirming that data migration is complete for this product and property. Do you wish to proceed? This cannot be undone."
                    : "Are you sure to submit all the information ? This cannot be undone !"}
                </Typography>
                <Box className="dashboard-modal-actions">
                  <Button variant="outlined" onClick={SubmitPayload}>
                    Yes
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setIsSubmitPopupEnabled(false);
                    }}
                  >
                    No
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
        </Stack>
      )}
    </>
    </TooltipProvider>
  );
};

export default Dashboard;
